import React, { useEffect, useState, useRef } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Share2, Send, Settings2, ImageIcon, RefreshCw, Eye,
  CheckCircle2, XCircle, Facebook, Instagram, Upload,
  Clock, Calendar, Zap, Loader2,
} from "lucide-react";

const DAY_OPTIONS = [
  { value: "monday",    label: "Montag" },
  { value: "tuesday",   label: "Dienstag" },
  { value: "wednesday", label: "Mittwoch" },
  { value: "thursday",  label: "Donnerstag" },
  { value: "friday",    label: "Freitag" },
  { value: "saturday",  label: "Samstag" },
  { value: "sunday",    label: "Sonntag" },
];

export default function SocialMediaPage() {
  const { token } = useAuth();
  const [config, setConfig]   = useState(null);
  const [form, setForm]       = useState({});
  const [previewReleases, setPreviewReleases] = useState(null);
  const [previewImage, setPreviewImage]       = useState(null);
  const [posting, setPosting]                 = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState("setup");
  const overlayRef            = useRef();

  useEffect(() => {
    if (!token) return;
    loadConfig();
  }, [token]);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/social/config");
      setConfig(data);
      setForm({
        facebook_page_id:     data.facebook_page_id     || "",
        facebook_page_token:  "",
        instagram_account_id: data.instagram_account_id || "",
        post_day:             data.post_day    || "monday",
        post_hour:            data.post_hour   ?? 15,
        post_minute:          data.post_minute ?? 0,
        enabled_facebook:     data.enabled_facebook  || false,
        enabled_instagram:    data.enabled_instagram || false,
        caption_template:     data.caption_template  || "",
        lookback_days:        data.lookback_days      || 7,
        cover_size_pct:       data.cover_size_pct     ?? 42,
        cover_center_y_pct:   data.cover_center_y_pct ?? 50,
        show_text:            data.show_text           ?? false,
      });
    } catch { toast.error("Config konnte nicht geladen werden."); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.facebook_page_token) delete payload.facebook_page_token;
      await api.put("/social/config", payload);
      toast.success("Einstellungen gespeichert.");
      loadConfig();
    } catch { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const handleOverlayUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/social/overlay", fd);
      setConfig(c => ({ ...c, overlay_url: data.url }));
      toast.success("Overlay hochgeladen!");
    } catch { toast.error("Upload fehlgeschlagen."); }
  };

  const handleResetPosted = async () => {
    if (!window.confirm("Posting-History zurücksetzen? Beim nächsten Post werden alle Releases erneut berücksichtigt.")) return;
    try {
      await api.post("/social/reset-posted");
      toast.success("History zurückgesetzt.");
      loadConfig();
    } catch { toast.error("Reset fehlgeschlagen."); }
  };

  const loadPreviewReleases = async () => {
    setLoadingReleases(true);
    try {
      const { data } = await api.get("/social/preview-releases");
      setPreviewReleases(data);
    } catch { toast.error("Vorschau fehlgeschlagen."); }
    finally { setLoadingReleases(false); }
  };

  const handlePreviewImage = async (cover_url, artist = "", titel = "") => {
    setGeneratingPreview(true);
    setPreviewImage(null);
    try {
      const { data } = await api.post("/social/preview-image", {
        cover_url,
        overlay_url: config?.overlay_url,
        artist,
        titel,
      });
      setPreviewImage(data.url);
    } catch { toast.error("Bild-Generierung fehlgeschlagen."); }
    finally { setGeneratingPreview(false); }
  };

  const [postResult, setPostResult] = useState(null);

  const handlePostNow = async (force = false) => {
    const msg = force
      ? "Force-Post: Duplikat-Check wird übersprungen – bereits gepostete Releases werden ERNEUT gepostet. Fortfahren?"
      : "Jetzt sofort posten? Dies sendet echte Posts auf Facebook/Instagram.";
    if (!window.confirm(msg)) return;
    setPosting(true);
    setPostResult(null);
    try {
      const { data } = await api.post("/social/post-now", { force });
      setPostResult(data);
      if (data.skipped) {
        toast.info(data.reason || "Nichts zu posten.");
      } else {
        const errors = (data.results || []).filter(r => r.error);
        const success = (data.results || []).filter(r => !r.error);
        if (errors.length === 0) toast.success(`Erfolgreich gepostet! ${data.images_generated} Bilder, ${data.cdn_urls_obtained ?? 0} CDN-URLs.`);
        else if (success.length === 0) toast.error("Alle Plattformen fehlgeschlagen – Details unten.");
        else toast.warning("Teilweise gepostet – Details unten.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Posting fehlgeschlagen.";
      toast.error(detail);
      setPostResult({ error: detail });
    } finally { setPosting(false); }
  };

  const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-rose-500/50";
  const nextPostDay = DAY_OPTIONS.find(d => d.value === form.post_day)?.label ?? "Montag";

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        {/* Page header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black text-white font-heading flex items-center gap-2">
              <Share2 size={22} className="text-rose-400" /> Social Media
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Automatisches Posting neuer Releases auf Facebook & Instagram
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handlePostNow(false)} disabled={posting}
              className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
              <Zap size={14} className={posting ? "animate-pulse" : ""} />
              {posting ? "Posting…" : "Jetzt posten"}
            </button>
            <button onClick={() => handlePostNow(true)} disabled={posting}
              title="Überspringt Duplikat-Check – bereits gepostete Releases werden erneut gepostet (nur für Tests)"
              className="flex items-center gap-2 px-3 py-2.5 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white hover:text-white text-sm font-medium rounded-lg transition-colors">
              <RefreshCw size={13} />
              Force
            </button>
          </div>
        </div>

        {/* Post-Ergebnis Panel */}
        {postResult && !postResult.skipped && (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900/80 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Posting-Ergebnis</p>
              <button onClick={() => setPostResult(null)} className="text-xs text-zinc-600 hover:text-zinc-400">✕</button>
            </div>
            {postResult.error && (
              <p className="text-xs text-rose-400 bg-rose-950/30 rounded-lg px-3 py-2">{postResult.error}</p>
            )}
            {!postResult.error && (
              <div className="flex gap-4 text-xs text-zinc-400">
                <span>Releases: <b className="text-white">{postResult.releases_count}</b></span>
                <span>Bilder: <b className="text-white">{postResult.images_generated}</b></span>
                {postResult.cdn_urls_obtained != null && (
                  <span>CDN-URLs: <b className={postResult.cdn_urls_obtained === postResult.images_generated ? "text-emerald-400" : "text-amber-400"}>
                    {postResult.cdn_urls_obtained}/{postResult.images_generated}
                  </b></span>
                )}
                {postResult.skipped_already_posted > 0 && (
                  <span>Übersprungen: <b className="text-zinc-500">{postResult.skipped_already_posted}</b></span>
                )}
              </div>
            )}
            {(postResult.results || []).map((r, i) => (
              <div key={i} className={`flex items-start gap-3 rounded-lg px-3 py-2.5 text-xs ${
                r.error ? "bg-rose-950/30 border border-rose-500/20" : "bg-emerald-950/20 border border-emerald-500/20"
              }`}>
                {r.platform === "facebook"
                  ? <Facebook size={13} className={r.error ? "text-rose-400 mt-0.5 flex-shrink-0" : "text-blue-400 mt-0.5 flex-shrink-0"} />
                  : <Instagram size={13} className={r.error ? "text-rose-400 mt-0.5 flex-shrink-0" : "text-pink-400 mt-0.5 flex-shrink-0"} />
                }
                <div className="flex-1 min-w-0">
                  <span className="font-semibold capitalize">{r.platform}: </span>
                  {r.error
                    ? <span className="text-rose-300 break-all">{r.error}</span>
                    : <span className="text-emerald-300">Erfolgreich {r.type === "carousel" ? `(${r.slides} Slides)` : ""} · ID: {r.media_id}</span>
                  }
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Status-Chips */}
        {config && (
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${
              form.enabled_facebook ? "bg-blue-500/10 border-blue-500/20 text-blue-400" : "bg-zinc-800/60 border-zinc-700 text-zinc-500"
            }`}>
              <Facebook size={11} /> Facebook {form.enabled_facebook ? "aktiv" : "inaktiv"}
            </span>
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${
              form.enabled_instagram ? "bg-pink-500/10 border-pink-500/20 text-pink-400" : "bg-zinc-800/60 border-zinc-700 text-zinc-500"
            }`}>
              <Instagram size={11} /> Instagram {form.enabled_instagram ? "aktiv" : "inaktiv"}
            </span>
            {config.last_post_at && (
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 px-3 py-1 rounded-full border border-zinc-700">
                <Clock size={11} /> Letzter Post: {new Date(config.last_post_at).toLocaleDateString("de-DE")}
              </span>
            )}
          </div>
        )}

        {!config && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 size={15} className="animate-spin" /> Lade Einstellungen…
          </div>
        )}

        {/* Tabs */}
        {config && (
          <>
            <div className="flex gap-1 border-b border-zinc-800">
              {[
                { id: "setup",    label: "Accounts",  icon: Settings2 },
                { id: "schedule", label: "Zeitplan",  icon: Calendar },
                { id: "overlay",  label: "Overlay",   icon: ImageIcon },
                { id: "caption",  label: "Caption",   icon: Send },
              ].map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    tab === id
                      ? "border-rose-500 text-white"
                      : "border-transparent text-zinc-500 hover:text-zinc-300"
                  }`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>

            {/* Tab: Accounts */}
            {tab === "setup" && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-white">Facebook & Instagram</h2>
                </div>
                <div className="p-5 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-300 font-medium block mb-2">Facebook Page ID</label>
                      <input className={inp} value={form.facebook_page_id || ""}
                        onChange={e => setForm(f => ({ ...f, facebook_page_id: e.target.value }))}
                        placeholder="123456789012345" />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-300 font-medium block mb-2">Instagram Business Account ID</label>
                      <input className={inp} value={form.instagram_account_id || ""}
                        onChange={e => setForm(f => ({ ...f, instagram_account_id: e.target.value }))}
                        placeholder="123456789" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-zinc-300 font-medium block mb-2">Page Access Token (Long-Lived)</label>
                    <input className={inp} type="password" value={form.facebook_page_token || ""}
                      onChange={e => setForm(f => ({ ...f, facebook_page_token: e.target.value }))}
                      placeholder="Neuen Token eingeben (leer lassen = bestehender bleibt)" />
                    <p className="text-xs text-zinc-500 mt-1.5">
                      Meta Developer Dashboard → Graph API Explorer → Page Token → als Long-Lived Token konvertieren.
                    </p>
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.enabled_facebook || false}
                        onChange={e => setForm(f => ({ ...f, enabled_facebook: e.target.checked }))}
                        className="accent-blue-500 w-4 h-4" />
                      <span className="text-sm text-zinc-300">Facebook aktivieren</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.enabled_instagram || false}
                        onChange={e => setForm(f => ({ ...f, enabled_instagram: e.target.checked }))}
                        className="accent-pink-500 w-4 h-4" />
                      <span className="text-sm text-zinc-300">Instagram aktivieren</span>
                    </label>
                  </div>

                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                    Speichern
                  </button>
                </div>
              </div>
            )}

            {/* Tab: Zeitplan */}
            {tab === "schedule" && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-white">Posting-Zeitplan</h2>
                </div>
                <div className="p-5 space-y-5">
                  <p className="text-sm text-zinc-400">
                    Posts werden nur ausgelöst wenn es neue Releases im gewählten Zeitraum gibt.
                    Keine neuen Releases = kein Post.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-zinc-300 font-medium block mb-2">Wochentag</label>
                      <select className={inp} value={form.post_day || "monday"}
                        onChange={e => setForm(f => ({ ...f, post_day: e.target.value }))}>
                        {DAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-300 font-medium block mb-2">Uhrzeit (Stunde)</label>
                      <select className={inp} value={form.post_hour ?? 15}
                        onChange={e => setForm(f => ({ ...f, post_hour: +e.target.value }))}>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{String(i).padStart(2, "0")}:00 Uhr</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-300 font-medium block mb-2">Minute</label>
                      <select className={inp} value={form.post_minute ?? 0}
                        onChange={e => setForm(f => ({ ...f, post_minute: +e.target.value }))}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-zinc-300 font-medium block mb-2">Releases der letzten … Tage berücksichtigen</label>
                    <select className={`w-40 ${inp}`} value={form.lookback_days || 7}
                      onChange={e => setForm(f => ({ ...f, lookback_days: +e.target.value }))}>
                      {[3, 5, 7, 10, 14].map(d => <option key={d} value={d}>{d} Tage</option>)}
                    </select>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">
                    <p className="text-xs text-zinc-400">
                      Nächster geplanter Post: <span className="text-white font-semibold">
                        {nextPostDay} {String(form.post_hour ?? 15).padStart(2, "0")}:{String(form.post_minute ?? 0).padStart(2, "0")} Uhr
                      </span>
                    </p>
                  </div>

                  {/* Duplikat-Schutz Info */}
                  <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold text-zinc-300">Duplikat-Schutz aktiv</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {config.posted_release_ids?.length > 0
                          ? <><span className="text-white font-medium">{config.posted_release_ids.length}</span> Releases bereits gepostet – werden beim nächsten Post übersprungen.</>
                          : "Noch keine Releases gepostet."
                        }
                      </p>
                    </div>
                    {config.posted_release_ids?.length > 0 && (
                      <button onClick={handleResetPosted}
                        className="text-xs text-zinc-500 hover:text-rose-400 border border-zinc-700 hover:border-rose-500/50 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0">
                        History zurücksetzen
                      </button>
                    )}
                  </div>

                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                    Zeitplan speichern
                  </button>
                </div>
              </div>
            )}

            {/* Tab: Overlay */}
            {tab === "overlay" && (
              <div className="space-y-4">
                <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5">
                    <h2 className="text-sm font-semibold text-white">Overlay-PNG</h2>
                  </div>
                  <div className="p-5 space-y-4">
                    <p className="text-sm text-zinc-400">
                      Transparentes PNG (HUWND-Rahmen, Logo) wird über das Release-Cover gelegt.
                      Empfehlung: In Canva erstellen, als PNG exportieren. Optimal: 1080×1080px.
                    </p>
                    {config?.overlay_url && (
                      <div className="flex items-center gap-3">
                        <img src={config.overlay_url} alt="Overlay" className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />
                        <div>
                          <p className="text-sm text-zinc-300 font-medium">Aktuelles Overlay</p>
                          <p className="text-xs text-zinc-600 break-all">{config.overlay_url.split("/").pop()}</p>
                        </div>
                      </div>
                    )}
                    <button onClick={() => overlayRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white text-sm rounded-lg transition-colors">
                      <Upload size={14} /> Template hochladen
                    </button>
                    <input ref={overlayRef} type="file" accept="image/png,image/webp,image/jpeg" className="hidden" onChange={handleOverlayUpload} />

                    {/* Cover-Position */}
                    <div className="pt-2 border-t border-zinc-800 space-y-4">
                      <p className="text-sm font-medium text-zinc-300">Cover-Platzierung im Template</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-zinc-500 block mb-1.5">Cover-Größe ({form.cover_size_pct ?? 42}% der Bildbreite)</label>
                          <input type="range" min="20" max="75" value={form.cover_size_pct ?? 42}
                            onChange={e => setForm(f => ({ ...f, cover_size_pct: +e.target.value }))}
                            className="w-full accent-rose-500" />
                          <div className="flex justify-between text-[10px] text-zinc-600 mt-0.5">
                            <span>Klein (20%)</span><span>Groß (75%)</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500 block mb-1.5">Vertikale Position (Mitte bei {form.cover_center_y_pct ?? 50}%)</label>
                          <input type="range" min="20" max="80" value={form.cover_center_y_pct ?? 50}
                            onChange={e => setForm(f => ({ ...f, cover_center_y_pct: +e.target.value }))}
                            className="w-full accent-rose-500" />
                          <div className="flex justify-between text-[10px] text-zinc-600 mt-0.5">
                            <span>Oben</span><span>Unten</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={form.show_text ?? true}
                            onChange={e => setForm(f => ({ ...f, show_text: e.target.checked }))}
                            className="accent-rose-500 w-4 h-4" />
                          <span className="text-sm text-zinc-300">Artist + Titel auf Bild anzeigen</span>
                        </label>
                      </div>
                      <button onClick={handleSave} disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors">
                        Position speichern
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-white">Bild-Vorschau</h2>
                    <button onClick={loadPreviewReleases} disabled={loadingReleases}
                      className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white border border-zinc-700 hover:border-zinc-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                      <RefreshCw size={11} className={loadingReleases ? "animate-spin" : ""} /> Releases laden
                    </button>
                  </div>
                  <div className="p-5">
                    {!previewReleases ? (
                      <p className="text-sm text-zinc-500">Klicke "Releases laden" um Vorschau zu sehen.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {previewReleases.releases.slice(0, 8).map(r => {
                          const sz = form.cover_size_pct ?? 42;
                          const cy = form.cover_center_y_pct ?? 50;
                          return (
                          <div key={r.id} className="space-y-1">
                            <div className="relative group aspect-square rounded-lg overflow-hidden bg-zinc-800">
                              {/* Hintergrund: Overlay-Template oder Cover wenn kein Template */}
                              {config?.overlay_url
                                ? <img src={config.overlay_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                : r.cover_url && <img src={r.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                              }
                              {/* Release-Cover zentriert über dem Template */}
                              {config?.overlay_url && r.cover_url && (
                                <img
                                  src={r.cover_url}
                                  alt=""
                                  className="absolute object-cover"
                                  style={{
                                    width:  `${sz}%`,
                                    height: `${sz}%`,
                                    left:   `${50 - sz / 2}%`,
                                    top:    `${cy - sz / 2}%`,
                                  }}
                                />
                              )}
                              <button onClick={() => handlePreviewImage(r.cover_url, r.interpret, r.titel)}
                                disabled={generatingPreview}
                                className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Eye size={16} className="text-white" />
                              </button>
                            </div>
                            <p className="text-[11px] text-zinc-500 truncate">{r.interpret}</p>
                          </div>
                          );
                        })}
                      </div>
                    )}
                    {generatingPreview && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-3">
                        <Loader2 size={12} className="animate-spin" /> Bild wird generiert…
                      </div>
                    )}
                    {previewImage && (
                      <div className="mt-4">
                        <p className="text-xs text-zinc-500 mb-2">Generiertes Post-Bild (1080×1080):</p>
                        <img src={previewImage} alt="Vorschau" className="max-w-xs rounded-xl border border-zinc-700" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Caption */}
            {tab === "caption" && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-white">Caption-Template</h2>
                </div>
                <div className="p-5 space-y-4">
                  <p className="text-sm text-zinc-400">
                    Platzhalter: <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 text-xs">{"{tracks}"}</code> wird durch die Release-Liste ersetzt.
                  </p>
                  <textarea rows={8} className={inp} value={form.caption_template || ""}
                    onChange={e => setForm(f => ({ ...f, caption_template: e.target.value }))} />
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                    Caption speichern
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
